import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Spinner, Form } from 'react-bootstrap';
import config from '../../Common/Configurations/APIConfig';
import apiService from '../../Common/Configurations/APIService';
import './HolidayScreen.css';
import { LandingContext } from '../Profile/LandingContext';
import DefaultData from '../../CompanyData/DefaultData';

const destinationImages = [
    'https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?q=80&w=1966',
    'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=600',
    'https://images.unsplash.com/photo-1534067783941-51c9c23ecefd?q=80&w=1974',
    'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?q=80&w=2070',
    'https://images.unsplash.com/photo-1526772662000-3f88f10405ff?q=80&w=1974',
    'https://images.unsplash.com/photo-1542051841857-5f90071e7989?q=80&w=2070',
    'https://plus.unsplash.com/premium_photo-1697729438401-fcb4ff66d9a8?w=600',
    'https://images.unsplash.com/photo-1736099513064-dc772ccc1d81?w=600',
];

const travelTypeVisuals = {
    default:    { icon: <i className="ri-compass-3-line" style={{ fontSize: 40 }} />,   color: '#2D93D3' },
    group:      { icon: <i className="ri-group-line" style={{ fontSize: 40 }} />,        color: '#10B981' },
    corporate:  { icon: <i className="ri-building-4-line" style={{ fontSize: 40 }} />,   color: '#0dcaf0' },
    honeymoon:  { icon: <i className="ri-heart-line" style={{ fontSize: 40 }} />,        color: '#dc3545' },
    pilgrims:   { icon: <i className="ri-earth-line" style={{ fontSize: 40 }} />,        color: '#C8A84B' },
};

/* ── Hero with search ─────────────────────────────────────── */
const HeroSection = ({ travelTypes }) => {
    const landingData = useContext(LandingContext);
    const navigate = useNavigate();
    const [destination, setDestination] = useState('');
    const [travelType, setTravelType]   = useState('');
    const [month, setMonth]             = useState('');
    const [suggestions, setSuggestions]   = useState([]);
    const [isSearching, setIsSearching]   = useState(false);
    const [itemSelected, setItemSelected] = useState(false);
    const searchContainerRef = useRef(null);

    const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];

    useEffect(() => {
        if (destination.trim() === '' || !DefaultData.DEFAULTCOMPANYID || itemSelected) { setSuggestions([]); return; }
        const timer = setTimeout(async () => {
            setIsSearching(true);
            try {
                const res = await apiService.getAll(`${config.GETDESTINATIONSEARCH}?keyword=${destination}&companyId=${DefaultData.DEFAULTCOMPANYID}`);
                setSuggestions(res || []);
            } catch { setSuggestions([]); }
            finally { setIsSearching(false); }
        }, 300);
        return () => clearTimeout(timer);
    }, [destination, itemSelected]);

    useEffect(() => {
        const handler = (e) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) setSuggestions([]);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        setSuggestions([]);
        navigate(`/holidays/search?${new URLSearchParams({ destination, traveltype: travelType, month }).toString()}`);
    };

    const heroStyle = {
        backgroundImage: landingData?.holidayBackgroundSrc
            ? `url(${landingData.holidayBackgroundSrc})`
            : 'url(/assets/images/holidaysearch.jpg)',
    };

    return (
        <div className="holiday-hero-section" style={heroStyle}>
            <Container>
                <h1 className="display-4 fw-bold text-white">Find Your Perfect Trip</h1>
                <p className="lead mb-5">Curated packages for every kind of traveller</p>
                <div className="hero-search-container">
                    <Form onSubmit={handleSearch} className="holiday-search-bar">
                        <Row className="g-3 align-items-end">
                            <Col md={4} ref={searchContainerRef} style={{ position: 'relative' }}>
                                <Form.Label>Destination</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g., Goa, Kerala, Maldives"
                                    value={destination}
                                    onChange={e => { setItemSelected(false); setDestination(e.target.value); }}
                                    autoComplete="off"
                                />
                                {(suggestions.length > 0 || isSearching) && (
                                    <div className="autocomplete-suggestions">
                                        {isSearching && <div className="suggestion-item">Searching…</div>}
                                        {!isSearching && suggestions.map(s => (
                                            <div key={s.destinationId} className="suggestion-item"
                                                onClick={() => { setItemSelected(true); setDestination(s.destinationName); setSuggestions([]); }}>
                                                {s.destinationName}
                                            </div>
                                        ))}
                                        {!isSearching && suggestions.length === 0 && destination.trim() !== '' && (
                                            <div className="suggestion-item">No results found</div>
                                        )}
                                    </div>
                                )}
                            </Col>
                            <Col md={3}>
                                <Form.Label>Travel Type</Form.Label>
                                <Form.Select value={travelType} onChange={e => setTravelType(e.target.value)}>
                                    <option value="">All Types</option>
                                    {travelTypes.map(t => (
                                        <option key={t.travelTypeId} value={t.travelTypeName.toLowerCase()}>{t.travelTypeName}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Label>Month</Form.Label>
                                <Form.Select value={month} onChange={e => setMonth(e.target.value)}>
                                    <option value="">Any Month</option>
                                    {months.map(m => <option key={m} value={m.toLowerCase()}>{m}</option>)}
                                </Form.Select>
                            </Col>
                            <Col md={2} className="d-flex align-items-end">
                                <Button type="submit" className="btn-primary w-100">
                                    <i className="ri-search-line me-1" /> Search
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </div>
            </Container>
        </div>
    );
};

/* ── Destinations Section ─────────────────────────────────── */
const DestinationsSection = ({ destinations }) => (
    <section className="mb-5 pb-4 fade-in-section">
        <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="section-title mb-0 text-start">Popular Destinations</h2>
        </div>
        <Row xs={1} md={2} lg={4} className="g-4">
            {destinations.slice(0, 8).map((dest, i) => (
                <Col key={dest.destinationId}>
                    <Link to={`/destination/${dest.destinationId}`} className="destination-card-modern">
                        <img
                            src={dest.imageSrc || destinationImages[i % destinationImages.length]}
                            alt={dest.destinationName}
                            className="card-img"
                        />
                        <div className="card-img-overlay">
                            <h4 className="card-title">{dest.destinationName}</h4>
                        </div>
                    </Link>
                </Col>
            ))}
        </Row>
    </section>
);

/* ── Travel Types Section ─────────────────────────────────── */
const TravelTypesSection = ({ travelTypes }) => {
    if (!travelTypes || travelTypes.length === 0) return null;
    return (
        <section className="mb-5 pb-4 fade-in-section">
            <h2 className="section-title">Travel Your Way</h2>
            <Row xs={2} md={3} lg={5} className="g-4 justify-content-center">
                {travelTypes.map(type => {
                    const visual = travelTypeVisuals[type.travelTypeName.toLowerCase()] || travelTypeVisuals.default;
                    return (
                        <Col key={type.travelTypeId}>
                            <Link
                                to={`/packages/type/${type.travelTypeName.toLowerCase()}`}
                                className="travel-type-card"
                                style={{ '--card-hover-color': visual.color }}
                            >
                                <div className="travel-type-icon">{visual.icon}</div>
                                <Card.Title style={{ fontFamily: 'Inter,sans-serif', fontWeight: 700, fontSize: '1rem', margin: 0 }}>
                                    {type.travelTypeName}
                                </Card.Title>
                            </Link>
                        </Col>
                    );
                })}
            </Row>
        </section>
    );
};

/* ── Packages Section ─────────────────────────────────────── */
const PackagesSection = ({ packages }) => (
    <section className="fade-in-section">
        <h2 className="section-title">Trending Packages</h2>
        <Row xs={1} md={2} lg={3} className="g-4">
            {packages.slice(0, 6).map(pkg => (
                <Col key={pkg.packageId}>
                    <Card className="package-card h-100">
                        <Card.Img
                            variant="top"
                            src={pkg.imageSrc || 'https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=400'}
                            alt={pkg.packageName}
                        />
                        <Card.Body className="d-flex flex-column">
                            <Card.Title className="fw-bold mb-1">
                                <Link to={`/holiday-details/${pkg.packageId}`} className="card-title-link">
                                    {pkg.packageName}
                                </Link>
                            </Card.Title>
                            <div className="icon-text mb-2">
                                <i className="ri-map-pin-2-line" style={{ color: '#2D93D3' }} />
                                <span>{pkg.destinationsCover}</span>
                            </div>
                            <div className="mt-auto pt-3">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="icon-text fw-medium">
                                        <i className="ri-calendar-2-line" style={{ color: '#2D93D3' }} />
                                        <span>{pkg.duration}</span>
                                    </div>
                                    <span className="price-tag">{pkg.currency} {pkg.price}</span>
                                </div>
                                <div className="d-grid">
                                    <Button as={Link} to={`/holiday-details/${pkg.packageId}`} className="btn-primary">
                                        View Details
                                    </Button>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            ))}
        </Row>
    </section>
);

/* ── Main Page ────────────────────────────────────────────── */
const HolidaysPage = () => {
    const [destinations, setDestinations] = useState([]);
    const [packages, setPackages]         = useState([]);
    const [travelTypes, setTravelTypes]   = useState([]);
    const [isLoading, setIsLoading]       = useState(true);
    const companyId = DefaultData.DEFAULTCOMPANYID;

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const [destRes, typesRes, pkgsRes] = await Promise.all([
                    apiService.getAll(config.GETALLDESTINATIONS),
                    apiService.getAll(config.GETALLTRAVELTYPES),
                    apiService.getAll(`${config.GETPACKAGEBYCOMAPANYID}/${companyId}?type=Holiday`),
                ]);
                setDestinations(destRes);
                setTravelTypes(typesRes);
                setPackages(pkgsRes.data);
            } catch (err) {
                console.error('Failed to fetch holiday data:', err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [companyId]);

    return (
        <div className="main-content">
            <div className="page-content" style={{ paddingTop: 0 }}>
                <HeroSection travelTypes={travelTypes} />
                <Container className="my-5 py-4">
                    {isLoading ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '40vh' }}>
                            <Spinner animation="border" variant="primary" />
                            <span className="ms-2 fs-5">Loading packages…</span>
                        </div>
                    ) : (
                        <>
                            <DestinationsSection destinations={destinations} />
                            <TravelTypesSection  travelTypes={travelTypes} />
                            <PackagesSection     packages={packages} />
                        </>
                    )}
                </Container>
            </div>
        </div>
    );
};

export default HolidaysPage;
